import { useState, useEffect, useRef } from "react";
import PortalConfetti from "./PortalConfetti";
import { doc, setDoc, getDoc, increment } from "firebase/firestore";
import { db } from "../firebase";
import { TRIVIA_MAP } from "../maps";
import { TRIVIA } from "../data";
import { getDailyItem, updateGameStats } from "../utils/gameLogic";
import { usePersistentStorage, useKeyboardNavigation } from "../hooks/useGameHooks";

/**
 * Trivia game mode — 2 guesses at a multiple-choice question.
 * Syncs results to Firestore for global stats. Anti-cheat effect sets hasWon flag.
 *
 * @param {{ version: string, targetDateObj: Date, targetDateStr: string, isArchive: boolean, setIsModalOpen: (v: boolean) => void, guessedOptions: Array<string>, setStoredGuesses: (g: Array<string>) => void }} props
 * @param {string} props.version - "old" or "new".
 * @param {Date} props.targetDateObj - Target date object.
 * @param {string} props.targetDateStr - Date string for localStorage keys.
 * @param {boolean} props.isArchive - Whether viewing an archived date.
 * @param {(v: boolean) => void} props.setIsModalOpen - Opens global stats modal.
 * @param {Array<string>} props.guessedOptions - Previously submitted options.
 * @param {(g: Array<string>) => void} props.setStoredGuesses - Guess state setter.
 * @returns {JSX.Element} The trivia game UI.
 */
export default function TriviaMode({
  version,
  targetDateObj,
  targetDateStr,
  isArchive,
  setIsModalOpen,
  guessedOptions,
  setStoredGuesses,
}) {
  const [collection, setCollection] = usePersistentStorage(
    "kisekidle-collection-trivia",
    []
  );
  const [isCopied, setIsCopied] = useState(false);
  const [targetTrivia] = useState(
    getDailyItem(TRIVIA, targetDateObj, TRIVIA_MAP)
  );

  const MAX_GUESSES = 2;
  const hasWon = guessedOptions.includes(targetTrivia.answer);
  const guessesLeft = MAX_GUESSES - guessedOptions.length;
  const isGameOver = guessesLeft <= 0 && !hasWon;

  // --- ANTI-CHEAT WIN FLAG ---
  useEffect(() => {
    if (hasWon) {
      const storageKey = `kisekidle-trivia-${targetDateStr}`;
      const storedData = JSON.parse(localStorage.getItem(storageKey) || "{}");
      storedData.hasWon = true;
      localStorage.setItem(storageKey, JSON.stringify(storedData));

      // Sync to collection if missing
      const selectedId = String(targetTrivia.id);
      if (!collection.some((id) => String(id) === selectedId)) {
        setCollection((prev) => [...prev, selectedId]);
      }
    }
  }, [hasWon, targetDateStr, targetTrivia.id]);

  const [globalStats, setGlobalStats] = useState(null);

  const syncInProgress = useRef(false);

  // --- TRIVIA TRACKING ---
  useEffect(() => {
    const syncReceipt = `synced-trivia-${targetDateStr}`;
    const hasAlreadySynced = localStorage.getItem(syncReceipt);

    if ((hasWon || isGameOver) && !hasAlreadySynced && !syncInProgress.current) {
      const updateAndFetchStats = async () => {
        syncInProgress.current = true;
        try {
          const statsRef = doc(db, "trivia_stats", targetDateStr);
          const updateData = {
            totalPlays: increment(1),
            totalWins: hasWon ? increment(1) : increment(0),
            totalWinningAttempts: hasWon
              ? increment(guessedOptions.length)
              : increment(0),
            guessTally: {},
          };
          guessedOptions.forEach((option) => {
            if (option) updateData.guessTally[option] = increment(1);
          });
          await setDoc(statsRef, updateData, { merge: true });
          localStorage.setItem(syncReceipt, "true");
          const updatedDoc = await getDoc(statsRef);
          if (updatedDoc.exists()) setGlobalStats(updatedDoc.data());
        } catch (error) {
          console.error("Firebase Error:", error);
        } finally {
          syncInProgress.current = false;
        }
      };
      updateAndFetchStats();
    }
  }, [hasWon, isGameOver, guessedOptions.length, targetDateStr]);

  /** Processes a trivia option selection — updates guesses, stats, and collection. */
  const handleGuess = (selectedOption) => {
    if (hasWon || isGameOver) return;
    if (guessedOptions.includes(selectedOption)) return;

    const newGuesses = [...guessedOptions, selectedOption];
    const isWin = selectedOption === targetTrivia.answer;
    const isLoss = newGuesses.length >= MAX_GUESSES && !isWin;

    if (!isArchive && (isWin || isLoss)) {
      if (typeof updateGameStats === "function") {
        updateGameStats("trivia", isWin, newGuesses.length, MAX_GUESSES);
      }
    }

    if (isWin) {
      const selectedId = String(targetTrivia.id);
      if (!collection.some((id) => String(id) === selectedId)) {
        setCollection([...collection, selectedId]);
      }
    }
    setStoredGuesses(newGuesses);
  };

  /** Copies trivia results (emoji or text format) to clipboard. */
  const handleShare = (format) => {
    const score = hasWon ? guessedOptions.length : "X";
    let shareText = `Kisekidle - Trivia (${targetDateStr})\nGuesses: ${score}/${MAX_GUESSES}\n\n`;
    
    if (format === "emoji") {
      const emojis = guessedOptions
        .map((guess) => (guess === targetTrivia.answer ? "🟩" : "🟥"))
        .join("");
      shareText += emojis;
    } else {
      guessedOptions.forEach((option, i) => {
        shareText += `${i + 1}. ${option} ${option === targetTrivia.answer ? "✅" : "❌"}\n`;
      });
    }

    shareText += `\n\nhttps://kisekidle.app/`;

    navigator.clipboard.writeText(shareText).then(() => {
      setIsCopied(format);
      setTimeout(() => setIsCopied(null), 2000);
    });
  };

  return (
    <div className="game-terminal trivia-terminal">
      <div className="header-container">
        <h2 className="mode-title">Daily Trivia</h2>
        <div className="guesses-remaining">
          Guesses Remaining: {guessesLeft}
        </div>
      </div>

      <div className="trivia-question">
        {targetTrivia.question}
      </div>

      <div className="trivia-options-grid">

        {targetTrivia.options &&
          targetTrivia.options.map((option, index) => {
            const isGuessed = guessedOptions.includes(option);
            const isCorrectAnswer = option === targetTrivia.answer;

            let btnBg = "#2d3446";
            let btnCursor = "pointer";
            let btnOpacity = 1;

            if (isGuessed) {
              btnCursor = "default";
              if (isCorrectAnswer) btnBg = "#4caf50";
              else {
                btnBg = "#ff4d4d";
                btnOpacity = 0.6;
              }
            } else if (hasWon || isGameOver) {
              btnCursor = "default";
              if (isCorrectAnswer) btnBg = "#4caf50";
              else btnOpacity = 0.4;
            }

            return (
              <button
                key={index}
                onClick={() => handleGuess(option)}
                disabled={isGuessed || hasWon || isGameOver}
                className={`trivia-option premium-hover ${isGuessed ? (isCorrectAnswer ? "correct" : "incorrect") : (hasWon || isGameOver) && isCorrectAnswer ? "correct" : ""}`}
                style={{
                  opacity: btnOpacity,
                }}
              >
                {option}
              </button>
            );
          })}
      </div>

      {hasWon && (
        <div className="victory-box">
          <PortalConfetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={false}
            numberOfPieces={400}
            gravity={0.15}
          />
          <h3>🎉 Correct! 🎉</h3>
          <p>Great job knowing your lore!</p>
          <div className="share-buttons-container">
            <button
              onClick={() => setIsModalOpen(true)}
              className="share-all-btn global-stats-trigger premium-hover"
            >
              📊 View Global Stats
            </button>

            <button className="share-all-btn premium-hover" onClick={() => handleShare("emoji")}>
              {isCopied === "emoji" ? "📋 Copied!" : "📤 Share Emojis"}
            </button>

            <button className="share-all-btn premium-hover" onClick={() => handleShare("text")}>
              {isCopied === "text" ? "📋 Copied!" : "📤 Share Text"}
            </button>
          </div>
        </div>
      )}

      {isGameOver && (
        <div className="game-over-box">
          <h3>❌ Out of guesses! ❌</h3>
          <p>
            The correct answer was <strong>{targetTrivia.answer}</strong>.
          </p>
          <div className="share-buttons-container">
            <button
              onClick={() => setIsModalOpen(true)}
              className="share-all-btn global-stats-trigger premium-hover"
            >
              📊 View Global Stats
            </button>

            <button className="share-all-btn premium-hover" onClick={() => handleShare("emoji")}>
              {isCopied === "emoji" ? "📋 Copied!" : "📤 Share Emojis"}
            </button>

            <button className="share-all-btn premium-hover" onClick={() => handleShare("text")}>
              {isCopied === "text" ? "📋 Copied!" : "📤 Share Text"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
